/**
 * Shared state hook for the Facilitator Portal.
 *
 * Manages facilitator auth, session data, and provides the typed
 * API wrapper so every sub-page can consume the same context.
 */
import { useState, useCallback, useEffect, useRef } from 'react';
import { schedulerApiClient, facilitatorProfileApi } from '@/lib/api';
import { onAuthExpired } from '@/lib/authSession';
import { clearTokens, saveTokens, scheduleQuietRenewal } from '@/lib/tokenRefresh';
import type {
  Facilitator,
  FacilitatorMeta,
  FacilitatorPage,
} from '@/types/facilitator-portal';
import { setHomeZone } from '@/lib/formatTime';
import { currentLocale, useI18n } from '@/lib/i18n';

// ─── Local-storage helpers ──────────────────────────────────────────────────

const STORAGE_KEY = 'playbook_facilitator_portal';
// schedulerApi (lib/api.ts) reads exactly this key for the Authorization
// header; FP login/register responses now include an access_token that
// we persist here so PUT/POST calls (availability, sessions, demand)
// authenticate against `require_auth`.
// 2026-07-19 (Item 8 / C-TOOL-INFRA, Fable finding): FP used to share
// the bare 'access_token' localStorage key with CC. On a shared origin
// (dev, staging behind one hostname) that meant a CC admin logging in
// would silently overwrite a facilitator's token and vice-versa —
// session cross-contamination between two very different roles.
// Namespaced to fp-access-token-v1. Migrates the old key once so
// currently-logged-in facilitators don't get bounced.
const TOKEN_KEY = 'fp-access-token-v1';
const LEGACY_TOKEN_KEY = 'access_token';
if (typeof window !== 'undefined') {
  try {
    const legacy = window.localStorage.getItem(LEGACY_TOKEN_KEY);
    const current = window.localStorage.getItem(TOKEN_KEY);
    if (legacy && !current) {
      window.localStorage.setItem(TOKEN_KEY, legacy);
    }
    // Clear the legacy key regardless — even if this session isn't FP,
    // this file only loads inside the FP shell, so the token here
    // belongs to a facilitator.
    if (legacy) window.localStorage.removeItem(LEGACY_TOKEN_KEY);
  } catch { /* private mode or storage disabled — skip migration */ }
}

function saveFacilitator(f: Facilitator) {
  try { localStorage.setItem(STORAGE_KEY, JSON.stringify(f)); } catch { /* noop */ }
}

function loadFacilitator(): Facilitator | null {
  try {
    const s = localStorage.getItem(STORAGE_KEY);
    return s ? (JSON.parse(s) as Facilitator) : null;
  } catch { return null; }
}

/**
 * A profile kept with no sign-in left to send (Quality, 28 Oct): an older
 * Studio removed only the access token on a 401, so every page then failed
 * with "check your connection". Start signed out (the leftover renewal
 * token too), and say why.
 */
function profileWithoutSignIn(): boolean {
  try {
    return !!localStorage.getItem(STORAGE_KEY) && !localStorage.getItem(TOKEN_KEY);
  } catch { return false; }
}

function clearFacilitator() {
  try { localStorage.removeItem(STORAGE_KEY); } catch { /* noop */ }
}

/** Sign-in answers carry the tokens; keep them in their own keys (lib/tokenRefresh). */
function saveSignInTokens(result: { access_token?: string; refresh_token?: string } | null) {
  if (result?.access_token) saveTokens(result.access_token, result.refresh_token);
}

// ─── Toast system ───────────────────────────────────────────────────────────

export interface Toast {
  id: number;
  msg: string;
  type: 'success' | 'error' | 'info' | 'warning';
  /** How long it stays, in ms; null = until dismissed. Drives the countdown line. */
  ttl: number | null;
}

// 2026-09-28 (Scoring dry-run finding P1): error toasts now persist
// on screen instead of auto-dismissing at 3.5s. The 500 during Nino's
// claim vanished before he could read it and the panel looked
// unresponsive. Errors stay until the user clicks them (dismiss on
// click, wired in ToastContainer). Success/info/warning keep the
// 3.5s auto-dismiss because they're not load-bearing to diagnose.
const AUTO_DISMISS_MS: Record<Toast['type'], number | null> = {
  success: 3500,
  info: 3500,
  warning: 6000,   // slightly longer than info — warnings often need reading
  error: null,     // persist until user dismisses
};

function useToast() {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const idRef = useRef(0);

  const toast = useCallback((msg: string, type: Toast['type'] = 'info') => {
    const id = ++idRef.current;
    const ttl = AUTO_DISMISS_MS[type];
    // 2026-09-29 audit: the same message used to stack (e.g. one error
    // every 30s refresh). Show it once; keep at most 4 on screen.
    setToasts((prev) => {
      if (prev.some((t) => t.msg === msg && t.type === type)) return prev;
      return [...prev, { id, msg, type, ttl }].slice(-4);
    });
    if (ttl != null) {
      setTimeout(() => setToasts((prev) => prev.filter((t) => t.id !== id)), ttl);
    }
  }, []);

  const dismissToast = useCallback((id: number) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  return { toasts, toast, dismissToast };
}

// ─── Enrich facilitator with _meta ──────────────────────────────────────────

function enrichFacilitator(raw: Record<string, unknown>): Facilitator {
  // The sign-in tokens live in their own keys; never copy them into the
  // saved profile (a stale refresh token there could be reused).
  const { access_token: _a, refresh_token: _r, token_type: _t, ...profile } = raw;
  void _a; void _r; void _t;
  const pd = profile.profile_data;
  const meta: FacilitatorMeta =
    typeof pd === 'string' ? JSON.parse(pd) : (pd as FacilitatorMeta) || {};
  return { ...(profile as unknown as Facilitator), _meta: meta };
}

// ─── Main hook ──────────────────────────────────────────────────────────────

export function useFacilitatorPortal() {
  const [signInLost] = useState(profileWithoutSignIn);
  const [facilitator, setFacilitatorRaw] = useState<Facilitator | null>(() => (signInLost ? null : loadFacilitator()));
  const [currentPage, setCurrentPage] = useState<FacilitatorPage>('dashboard');
  const { toasts, toast, dismissToast } = useToast();
  const { t } = useI18n();
  // Times show the profile zone too when it differs from this device's (lib/formatTime).
  setHomeZone(facilitator?.timezone);

  // Wrap setFacilitator to persist
  const setFacilitator = useCallback((f: Facilitator | null) => {
    setFacilitatorRaw(f);
    if (f) saveFacilitator(f);
    else clearFacilitator();
  }, []);

  useEffect(() => {
    if (!signInLost) return;
    clearTokens();
    clearFacilitator();
    toast(t('toast.expired'), 'warning');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // On mount: re-fetch facilitator from backend to get latest data
  useEffect(() => {
    if (!facilitator?.facilitator_id) return;
    scheduleQuietRenewal();
    schedulerApiClient
      .getFacilitator(facilitator.facilitator_id)
      .then((res) => {
        const enriched = enrichFacilitator({ ...res, _meta: facilitator._meta });
        setFacilitator(enriched);
      })
      .catch((err: Error) => {
        if (err.message?.includes('404') || err.message?.includes('not found')) {
          setFacilitator(null);
          toast(t('toast.session_expired_login'), 'warning');
        }
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Sign-in expired (any 401 on a signed-in request, see lib/authSession).
  // During a live drill: warn only; sign out when the console closes.
  useEffect(() => onAuthExpired(({ duringLiveDrill }) => {
    if (duringLiveDrill) {
      toast(t('toast.expired_during_drill'), 'warning');
      return;
    }
    clearTokens();
    setFacilitator(null);
    toast(t('toast.expired'), 'warning');
  }), [setFacilitator, toast, t]);

  // ── Auth actions ────────────────────────────────────────────────────
  const login = useCallback(async (email: string, password: string) => {
    const result = await schedulerApiClient.loginFacilitator({ email, password });
    // Persist the tokens so schedulerApi's request interceptor attaches
    // Authorization: Bearer <token>. Without this every PUT/POST 401s
    // with "Not authenticated".
    saveSignInTokens(result);
    const enriched = enrichFacilitator(result);
    setFacilitator(enriched);
    toast(t('toast.welcome_back', { name: enriched.display_name }), 'success');
    return enriched;
  }, [setFacilitator, toast, t]);

  const register = useCallback(async (data: Record<string, unknown>) => {
    const result = await schedulerApiClient.registerFacilitator(data as Parameters<typeof schedulerApiClient.registerFacilitator>[0]);
    // Sign-up returns no refresh token yet (asked Scoring 30 Sep), so this
    // sign-in lasts 60 minutes; the next code sign-in renews normally.
    saveSignInTokens(result);
    const profileData = data.profile_data as FacilitatorMeta | undefined;
    const enriched = enrichFacilitator({ ...result, _meta: profileData || {} });
    setFacilitator(enriched);
    toast(t('toast.welcome'), 'success');
    return enriched;
  }, [setFacilitator, toast, t]);

  // ── Email-code sign-in (Scoring 2026-09-29) ─────────────────────────
  const requestSignInCode = useCallback(
    // The sign-in email in the portal's language (Scoring 1 Oct: was always 'en').
    (email: string) => schedulerApiClient.requestFacilitatorCode(email, currentLocale()),
    [],
  );

  /** Checks the code and stores the token, but does NOT open the portal
   *  yet: a new facilitator first fills in their profile (finishSignIn). */
  const verifySignInCode = useCallback(async (email: string, code: string, timezone: string) => {
    const result = await schedulerApiClient.verifyFacilitatorCode(email, code, timezone);
    saveSignInTokens(result);
    return result as Record<string, unknown> & { display_name?: string; is_new_account?: boolean };
  }, []);

  /** Saves the profile (if any) through the facilitator's own route, then opens the portal. */
  const finishSignIn = useCallback(async (
    signedIn: Record<string, unknown>,
    profile: Record<string, unknown> | null,
  ) => {
    let raw = signedIn;
    if (profile) {
      const updated = await facilitatorProfileApi.updateMyAccount(profile);
      raw = { ...signedIn, ...updated };
    }
    const enriched = enrichFacilitator(raw);
    setFacilitator(enriched);
    toast(profile ? t('toast.welcome') : t('toast.welcome_back', { name: enriched.display_name }), 'success');
    return enriched;
  }, [setFacilitator, toast, t]);

  const logout = useCallback(() => {
    clearTokens();
    setFacilitator(null);
  }, [setFacilitator]);

  const updateFacilitator = useCallback(async (data: Record<string, unknown>) => {
    if (!facilitator) return;
    // The facilitator's own route (the admin route refuses facilitators).
    const { _meta: _ignored, ...allowed } = data as Record<string, unknown>;
    void _ignored;
    const result = await facilitatorProfileApi.updateMyAccount(allowed);
    const enriched = enrichFacilitator({
      ...facilitator,
      ...result,
      _meta: { ...facilitator._meta, ...(data._meta as FacilitatorMeta || data.profile_data as FacilitatorMeta || {}) },
    });
    setFacilitator(enriched);
    return enriched;
  }, [facilitator, setFacilitator]);

  return {
    facilitator,
    setFacilitator,
    updateFacilitator,
    requestSignInCode,
    verifySignInCode,
    finishSignIn,
    currentPage,
    setCurrentPage,
    toasts,
    toast,
    dismissToast,
    login,
    register,
    logout,
  };
}
